import { useState } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { misc, neutral } from "app/themes/colors";
import ExpandableTableCell from "components/expandable-table-cell";
import type { ProcessingEvent } from "services/types/processing-event.type";

import {
  PROCESSING_EVENTS_TABLE_COLUMN_COUNT,
  fieldLabelFromKey,
  formatCreatedAt,
  formatDetailValue,
  getEventTypeChipColor,
  isJsonLikeValue,
  sortedEventDetailKeys
} from "./processing-events-timeline.utils";

const iconSx = { ml: 1 };

interface ProcessingEventTableRowProps {
  event: ProcessingEvent;
}

export default function ProcessingEventTableRow({
  event
}: ProcessingEventTableRowProps) {
  const [expanded, setExpanded] = useState(false);
  const isFailed = event.event_type === "failed";

  return (
    <>
      <TableRow
        sx={(theme) => ({
          backgroundColor: isFailed
            ? alpha(theme.palette.error.light, 0.15)
            : expanded
              ? misc.lightMutedPurple
              : undefined,
          borderTop: expanded ? `solid 2px ${neutral[200]}` : undefined,
          borderLeft: expanded ? `solid 2px ${neutral[200]}` : undefined,
          borderRight: expanded ? `solid 2px ${neutral[200]}` : undefined
        })}
      >
        <ExpandableTableCell
          padding="none"
          setOpen={setExpanded}
          open={expanded}
          iconSx={iconSx}
        />
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          {formatCreatedAt(event.created_at)}
        </TableCell>
        <TableCell>{fieldLabelFromKey(event.operation)}</TableCell>
        <TableCell>
          <Chip
            label={event.event_type}
            color={getEventTypeChipColor(event.event_type)}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {event.error ?? event.message ?? ""}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          sx={{ py: 0, borderBottom: 0 }}
          colSpan={PROCESSING_EVENTS_TABLE_COLUMN_COUNT}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={(theme) => ({
                py: 2,
                px: 1,
                mb: 1,
                background: isFailed
                  ? alpha(theme.palette.error.light, 0.1)
                  : misc.lightMutedPurple,
                borderBottom: `solid 2px ${neutral[300]}`,
                borderLeft: `solid 2px ${neutral[300]}`,
                borderRight: `solid 2px ${neutral[300]}`,
                borderBottomLeftRadius: 6,
                borderBottomRightRadius: 6
              })}
            >
              <Box sx={{ p: 2, background: "white", borderRadius: "6px" }}>
                <Table size="small">
                  <TableBody>
                    {sortedEventDetailKeys(event).map((key) => {
                      const cellValue = event[key] as unknown;
                      const usePre = isJsonLikeValue(cellValue);
                      return (
                        <TableRow key={String(key)}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              fontWeight: 600,
                              width: "40%",
                              verticalAlign: "top"
                            }}
                          >
                            {fieldLabelFromKey(String(key))}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            <Typography
                              variant="body2"
                              component={usePre ? "pre" : "span"}
                              sx={{
                                fontFamily: usePre ? "monospace" : undefined,
                                whiteSpace: usePre ? "pre-wrap" : undefined,
                                wordBreak: "break-word"
                              }}
                            >
                              {formatDetailValue(cellValue)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
